import {Fragment, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Dialog, Transition} from '@headlessui/react'
import XIcon from '@heroicons/react/outline/XIcon'
import axios from "axios";
import ProductCard from "../product-card";
import Filters, {buildParams, clearFilters, toggleFilters} from "./filters";
import Pagination from "./pagination";
import {UseDebounce} from "@autocx/forms";
import {WebsiteContext} from "../../index";
import Sort, {getCatalogueSortFields} from "./sort";
import ActiveFilters from "./active-filters";
import classNames from "classnames";
import Theme from "../../theme";
import {buildESQueryBody, buildFiltersFromParam} from "./utils";
import {getBreadcrumbFromPath} from "./index";
import {buildProductSearchPayload, getProductListScopeKey} from "./search-scope";

export {buildProductSearchPayload, getProductListScopeKey} from "./search-scope";

const emptyProductsListData = {
    filters: [],
    products: [],
    pages: null,
    categories: []
};

const PRODUCT_LIST_CONTEXT_COOKIE = "acxProductListFilters";

const getSortFields = (storeSettings) => {
    const sortFields = storeSettings?.fields?.sort ? [...storeSettings.fields.sort] : [];

    if (storeSettings?.recommendedSort?.enabled) {
        sortFields.unshift('Recommended:recommended:recommended');
    }

    return sortFields;
};

const getProductDetailFilterQuery = (activeFilters = {}) => {
    const filters = {...activeFilters};
    delete filters.page;
    delete filters.sort;
    delete filters.order;
    delete filters['keyword-search'];

    if (!Object.keys(filters).length) return "";
    return buildParams(filters, false).slice(1);
};

const setProductListContextCookie = (queryString = "") => {
    if (typeof document === "undefined") return;

    if (!queryString) {
        document.cookie = `${PRODUCT_LIST_CONTEXT_COOKIE}=;max-age=0;path=/;SameSite=Lax`;
        return;
    }

    const context = {
        queryString,
        sourcePath: window.location.pathname,
        timestamp: Date.now()
    };

    document.cookie = `${PRODUCT_LIST_CONTEXT_COOKIE}=${encodeURIComponent(JSON.stringify(context))};max-age=600;path=/;SameSite=Lax`;
};

const isProductDetailLink = (href = "") => href.startsWith("product/") || href.includes("/product/");

function ProductsListLoadingState() {
    return (
        <div aria-hidden="true">
            <div className="flex flex-row items-center justify-between">
                <div className="products-list-skeleton-frame h-4 w-20 rounded"/>
                <div className="products-list-skeleton-frame h-4 w-28 rounded"/>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3 lg:gap-x-8">
                {[0, 1, 2].map((item) => (
                    <div key={item} className="overflow-hidden rounded border border-gray-200 bg-white shadow-sm">
                        <div className="products-list-skeleton-frame aspect-[4/3]"/>
                        <div className="space-y-2.5 p-4">
                            <div className="products-list-skeleton-frame h-4 w-4/5 rounded"/>
                            <div className="products-list-skeleton-frame h-4 w-2/3 rounded"/>
                            <div className="mt-4 h-px bg-gray-100"/>
                            <div className="products-list-skeleton-frame h-3 w-1/2 rounded"/>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ProductsListFiltersLoadingState() {
    return (
        <div className="hidden sm:block pt-6 pl-8 pr-4 mb-8" aria-hidden="true">
            <div className="space-y-7">
                <div className="space-y-3">
                    <div className="products-list-skeleton-frame h-4 w-20 rounded"/>
                    <div className="h-9 w-12 rounded border border-gray-200 bg-white"/>
                </div>
                <div className="space-y-3">
                    <div className="products-list-skeleton-frame h-4 w-16 rounded"/>
                    <div className="flex gap-2">
                        <div className="h-9 w-9 rounded border border-gray-200 bg-white"/>
                        <div className="h-9 w-9 rounded border border-gray-200 bg-white"/>
                    </div>
                </div>
                <div className="products-list-skeleton-frame h-px w-12"/>
                {[0, 1, 2].map((item) => (
                    <div key={item} className="space-y-3">
                        <div className="products-list-skeleton-frame h-4 w-24 rounded"/>
                        <div className="space-y-2">
                            <div className="products-list-skeleton-frame h-3 w-20 rounded"/>
                            <div className="products-list-skeleton-frame h-3 w-16 rounded"/>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function Design1(props) {
    const context = useContext(WebsiteContext)
    const breadCrumbs = useMemo(() => getBreadcrumbFromPath(props?.values), [props?.values]);
    const scopeKey = getProductListScopeKey(props?.values);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState(props?.values?.activeFilters ? props.values.activeFilters : {});
    const hasInitialData = !!props?.values?.data;
    const [data, setData] = useState(props?.values?.data ? props.values.data : emptyProductsListData);
    const [isLoading, setIsLoading] = useState(!!scopeKey && !hasInitialData);
    const productDetailFilterQuery = useMemo(() => getProductDetailFilterQuery(activeFilters), [activeFilters]);
    const handleProductListClickCapture = (event) => {
        const link = event.target?.closest?.('a[href]');
        const href = link?.getAttribute?.('href') || "";
        if (!isProductDetailLink(href)) return;

        setProductListContextCookie(productDetailFilterQuery);
    };

    const [size, setSize] = useState(24);
    const productsRef = useRef(null);
    const isFirstRender = useRef(hasInitialData);
    const activeScopeKeyRef = useRef(scopeKey);

    const search = UseDebounce(() => {
        const requestedScopeKey = getProductListScopeKey(props.values);
        if (!requestedScopeKey) return;

        const {criteria, aggsFilters} = buildESQueryBody(activeFilters, size, context?.website?.store);
        const requestData = buildProductSearchPayload({
            values: props.values,
            criteria,
            aggsFilters,
            activeFilters,
            builder: context.builder
        });

        setIsLoading(true);

        const requestConfig = {};
        const searchUrl = context.builder ? '/api/v2/website/products/search' : '/products/search';

        if (context.builder) {
            requestConfig.headers = {"X-Preview-Domain": `${location.protocol}//${context?.website?.previewDomain}`};
        }

        axios
            .post(searchUrl, requestData, requestConfig)
            .then(({data}) => {
                if (requestedScopeKey !== activeScopeKeyRef.current) return;

                setData({
                    filters: data.filters,
                    products: data.products,
                    pages: Math.ceil(data.products?.total?.value / size) || 1,
                    categories: data.categories,
                    storeOptions: data.storeSettingOptions
                })
                setIsLoading(false);
            })
            .catch((err) => {
                if (requestedScopeKey === activeScopeKeyRef.current) setIsLoading(false);
                if (!axios.isCancel(err)) console.log(err)
            });
    }, 300);

    useEffect(() => {
        const handlePopState = () => buildFiltersFromParam(
            setActiveFilters,
            null,
            props?.values?.fieldsList
        );

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [props?.values?.fieldsList]);

    useEffect(() => {
        activeScopeKeyRef.current = scopeKey;
        setActiveFilters(props?.values?.activeFilters || {});
        setData(props?.values?.data || emptyProductsListData);
        setIsLoading(!!scopeKey && !props?.values?.data);
        isFirstRender.current = !!props?.values?.data;
    }, [scopeKey]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return; // Skip the first run as the data is already loaded on the server
        }
        search();
    }, [activeFilters, scopeKey]);

    const isInitialLoading = isLoading && !data.products?.hits;

    if (data.products?.hits?.length === 0 && data.pages && +activeFilters['page'] > data.pages) {
        setActiveFilters((previous) => ({...previous, page: [data.pages]}))
    }

    return (
        <div>
            {/* Mobile filter */}
            <Transition.Root show={mobileFiltersOpen} as={Fragment}>
                <Dialog as="div" className={classNames(
                    "relative z-40 sm:hidden acx"
                )} onClose={setMobileFiltersOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black bg-opacity-25"/>
                    </Transition.Child>
                    <div className={classNames(
                        `heading_${context?.website?.theme?.selectedFont?.headingName?.split(' ').join('_').toLowerCase()}`,
                        `body_${context?.website?.theme?.selectedFont?.bodyName?.split(' ').join('_').toLowerCase()}`,
                        "fixed preview inset-0 z-40 flex"
                    )}>
                        <Theme settings={context?.website?.theme}/>
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="translate-x-full">
                            <Dialog.Panel
                                className="relative flex h-full w-full flex-col overflow-y-auto bg-white">
                                <div
                                    className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                                    <h3 className="!mb-0">Filters</h3>
                                    <button
                                        type="button"
                                        className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                                        onClick={() => setMobileFiltersOpen(false)}>
                                        <span className="sr-only">Close filter</span>
                                        <XIcon className="h-6 w-6 text-gray-700" aria-hidden="true"/>
                                    </button>
                                </div>
                                <Filters filters={data.filters}
                                         className={"p-4"}
                                         activeFilters={activeFilters} uri={props.values.uri}
                                         categories={data.categories}
                                         storeOptions={data.storeOptions}
                                         pathname={props.values.uri}
                                         setActiveFilters={setActiveFilters}/>
                                <div
                                    className={"sticky bottom-0 px-4 py-6 border-t section-white flex flex-row justify-between items-center gap-8"}>
                                    <button className={"button secondary w-full justify-center"}
                                            onClick={() => {
                                                clearFilters(activeFilters, setActiveFilters)
                                            }}>Clear Filters
                                    </button>
                                    <button className={"button w-full justify-center"}
                                            onClick={() => setMobileFiltersOpen(false)}>View&nbsp;
                                        {data.products?.total?.value !== undefined && data.products?.total?.value !== null && (
                                            data.products.total.value
                                        )}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>
            <div className="sm:mx-auto max-w-7xl min-h-screen" ref={productsRef}>
                <div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-4">
                        {isInitialLoading ? (
                            <ProductsListFiltersLoadingState/>
                        ) : data.products?.total?.value !== undefined && data.products?.total?.value !== null &&
                            <Filters filters={data.filters}
                                     className="pt-6 pl-8 pr-4 mb-8 overflow-y-auto sticky top-[var(--site-nav-height)] h-fit sm:max-h-[calc(100vh-var(--site-nav-height))] hidden sm:block"
                                     uri={props.values.uri}
                                     categories={data.categories}
                                     storeOptions={data.storeOptions}
                                     productsList={productsRef}
                                     pathname={props.values.uri}
                                     activeFilters={activeFilters} setActiveFilters={setActiveFilters}/>
                        }
                        <div className="sm:col-span-3 pb-24 pt-6 px-4 sm:pr-7 sm:!pl-0">
                            {isInitialLoading ? (
                                <ProductsListLoadingState/>
                            ) : (
                                <>
                                    {breadCrumbs && breadCrumbs.length > 1 &&
                                        <div
                                            className="flex flex-row items-center justify-between text-base font-medium text-gray-700 mb-2">
                                            <div>
                                                {breadCrumbs.map((crumb, i) => {
                                                    const isLast = i === breadCrumbs.length - 1;
                                                    return (
                                                        <span key={crumb.uri} className="inline text-sm">
                                                          {i > 0 && <span className="mx-1">/</span>}
                                                            {isLast ? (
                                                                <span className="opacity-75">{crumb.label}</span>
                                                            ) : (
                                                                <a className="text-colour-base" href={crumb.uri}>
                                                                    {crumb.label}
                                                                </a>
                                                            )}
                                                        </span>
                                                    );
                                                })}

                                            </div>
                                        </div>
                                    }
                                    <div
                                        className="flex flex-row items-center justify-between text-base font-medium text-gray-700">
                                        {data.products?.total?.value !== undefined && data.products?.total?.value !== null && (
                                            <div><span className={"font-semibold"}>{data.products.total.value}</span> {data.products.total.value === 1 ? 'result' : 'results'}
                                            </div>
                                        )}
                                        <div className="flex items-center">
                                            {/*Sort Menu*/}
                                            {context?.website?.store?.filters?.sort && data.products?.total?.value !== undefined && data.products?.total?.value !== null &&
                                                <Sort sortFields={props.values.catalogueNumber
                                                    ? getCatalogueSortFields(getSortFields(context?.website?.store))
                                                    : getSortFields(context?.website?.store)}
                                                      defaultOption={props.values.catalogueNumber
                                                          ? {label: "Catalogue Order", value: null}
                                                          : null}
                                                      activeFilters={activeFilters}
                                                      onClick={(v) => toggleFilters(v, 'sort', activeFilters, setActiveFilters, false, productsRef)}/>}
                                            {data.products?.total?.value !== undefined && data.products?.total?.value !== null &&
                                                <button
                                                    type="button"
                                                    className="ml-2 sm:!hidden block button"
                                                    onClick={() => setMobileFiltersOpen(true)}>
                                                    <span className="sr-only">Open Filter</span>
                                                    Filter
                                                </button>
                                            }
                                        </div>
                                    </div>
                                    {/*Active Filters*/}
                                    {data.products?.total?.value !== undefined && data.products?.total?.value !== null &&
                                        <ActiveFilters activeFilters={activeFilters}
                                                       setActiveFilters={setActiveFilters}
                                                       productsList={productsRef}
                                        />
                                    }
                                    {/*Product List*/}
                                    {data.products?.hits && (
                                        <div
                                            onClickCapture={handleProductListClickCapture}
                                            className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8">
                                            {data.products?.hits?.map((product) => (
                                                <ProductCard key={product._id} id={product._id} product={product._source}
                                                             settings={{design: product._source?.design}}
                                                             values={{
                                                                 options: {imageAspectRatio: context?.website?.store?.productCardAspectRatio},
                                                                 cardActionForms: data.products?.cardActionForms
                                                             }}/>
                                            ))}
                                        </div>
                                    )}
                                    {data.products?.hits?.length === 0 && (
                                        <div>
                                            <h4 className={"!mb-0 mt-2"}>No Results</h4>
                                            <p>Try removing some filters</p>
                                        </div>
                                    )}
                                    {data.products?.hits && (<div className="mt-6">
                                        <Pagination
                                            onPreviousClick={() => {
                                                const page = activeFilters['page'] ? +activeFilters['page'] - 1 : 1;
                                                toggleFilters(page, 'page', activeFilters, setActiveFilters, false, productsRef)
                                            }}
                                            onNextClick={() => {
                                                const page = activeFilters['page'] ? +activeFilters['page'] + 1 : 2;
                                                toggleFilters(page, 'page', activeFilters, setActiveFilters, false, productsRef)
                                            }}
                                            onPaginationClick={(pageNumber) => {
                                                toggleFilters(pageNumber, 'page', activeFilters, setActiveFilters, false, productsRef)
                                            }}
                                            pages={data.pages} currentPagination={activeFilters['page']}
                                        />
                                    </div>)}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {context?.website?.store?.productFootnote ? (
                <div className="sm:mx-auto max-w-7xl">
                    <div className="section text-xs text-gray-600 whitespace-pre-line">
                        <div className={"rich-text"}
                             dangerouslySetInnerHTML={{__html: context.website.store.productFootnote}}/>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
